import React, { memo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  AspectRatio,
  Box,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react"

import { useCart } from "@app/hooks/useCart"
import { useMedia } from "@app/hooks/useMedia"
import { FormButton } from "@app/components/Form/FormButton"
import { Icon } from "@app/components/Icon"
import ProductFormPrice from "@app/components/Product/Form/ProductFormPrice"
import { ProductFormVariants } from "@app/components/Product/Form/ProductFormVariants"
import { ProductStockOverlayForm } from "./ProductStockOverlayForm"

import type { ProductCombinedProps, ProductVariantProps, SelectedOptionProps } from "@root/types/global"
import { useGlobalContext } from "@app/providers/global"

type Props = {
  handleChange: (option: SelectedOptionProps) => void
  handleToggle: () => void
  isOpen: boolean
  product: ProductCombinedProps
  selectedOptions: Array<SelectedOptionProps>
  variant: ProductVariantProps
}

export const ProductStockOverlay: React.FC<Props> = memo(({ handleChange, handleToggle, isOpen, product, selectedOptions, variant }) => {
  const { additionalClose } = useGlobalContext()
  const { addToCart, loading } = useCart()
  const { product: locales } = useStaticQuery<GatsbyTypes.ProductStockOverlayQuery>(graphql`
    query ProductStockOverlay {
      product: sanityTemplateProduct {
        additionalStock
        additionalStockDescription
        additionalStockAvailable
      }
    }
  `)
  const { isSmall } = useMedia()
  const id = variant.id

  return (
    <Drawer isOpen={isOpen} placement="right" size="xl" onClose={handleToggle} returnFocusOnClose={false}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader bg="white" py={[5, 8]} px={[6, 14]}>
          <Flex align="flex-start" justifyContent="space-between">
            <Box>
              <Heading as="p" fontSize={["3xl", "32px"]} my={3} letterSpacing="ample" size="mainHeading">
                {locales?.additionalStock}
              </Heading>
              <Text size="xs" color="grey.coal" fontSize={"sm"} lineHeight={1.5} letterSpacing="generous" maxWidth={["full", "xs"]}>
                {locales?.additionalStockDescription}
              </Text>
            </Box>
            <FormButton onClick={handleToggle} title={additionalClose} aria-label={additionalClose} variant="ghost">
              <Icon name="close" width={5} />
            </FormButton>
          </Flex>
        </DrawerHeader>
        <DrawerBody as={VStack} alignItems="stretch" justifyContent="space-between" bg="white" p={0} spacing={0}>
          {!isSmall && (
            <VStack px={6} spacing={4}>
              <HStack alignItems="stretch" justifyContent="space-between" spacing={7.5}>
                <Box w={100}>
                  <AspectRatio bg="grey.cloud" ratio={100 / 140}>
                    {product?.images?.length > 0 && <Image {...product.images[0]} />}
                  </AspectRatio>
                </Box>
                <VStack alignItems="stretch" justifyContent="space-between" flex={1}>
                  <HStack alignItems="flex-start" justifyContent="space-between">
                    <VStack alignItems="fles-start" justifyContent="flex-start" flex={1} spacing={2}>
                      <Text letterSpacing="ample" size="3xl" lineHeight={1.2} fontSize="xl" color={"grey.coal"}>
                        {product?.title}
                      </Text>
                      {selectedOptions && (
                        <Text key={selectedOptions[0].name} letterSpacing="0.25px" size="sm" color="grey.coal" lineHeight={1.5} pt={1}>
                          {`${selectedOptions[0].name[0].toUpperCase()}${selectedOptions[0].name.slice(1)}: ${selectedOptions[0].value}`}
                        </Text>
                      )}
                    </VStack>
                    <VStack alignItems="flex-end" justifyContent="flex-start" flexShrink={0}>
                      <ProductFormPrice justifyContent="flex-end" size="3xl" fontSize="20px" letterSpacing="ample" color={"grey.coal"} />
                    </VStack>
                  </HStack>
                </VStack>
              </HStack>
              <FormButton
                type="submit"
                variant="findInStoreAddToCart"
                borderRadius="8px"
                w="100%"
                size="md"
                pt={3}
                pb={2.5}
                px={6}
                mx={6}
                isLoading={loading}
                onClick={event => {
                  event.stopPropagation()
                  addToCart(id, 1)
                }}
                title={locales?.additionalStockAvailable}
              >
                {locales?.additionalStockAvailable}
              </FormButton>
            </VStack>
          )}
          <HStack alignItems="stretch" justifyContent="space-between" spacing={7.5} px={[6, 14]} pb={8}>
            {isSmall && (
              <Box w={192}>
                <AspectRatio bg="grey.cloud" ratio={192 / 254}>
                  <>{product?.images?.length > 0 && <Image {...product.images[0]} />}</>
                </AspectRatio>
              </Box>
            )}
            <VStack alignItems="stretch" justifyContent="space-between" flex={1}>
              {isSmall && (
                <>
                  <VStack alignItems="stretch" justifyContent="space-between" flex={1}>
                    <HStack alignItems="flex-start" justifyContent="space-between">
                      <VStack alignItems="fles-start" justifyContent="flex-start" flex={1} spacing={2}>
                        <Text variant="medium" size="3xl" color="grey.coal" lineHeight={1.16}>
                          {product?.title}
                        </Text>
                        {selectedOptions && (
                          <Text key={selectedOptions[0].name} letterSpacing="0.25px" size="sm" color="grey.coal" lineHeight={1.5} pt={1}>
                            {`${selectedOptions[0].name[0].toUpperCase()}${selectedOptions[0].name.slice(1)}: ${selectedOptions[0].value}`}
                          </Text>
                        )}
                        <ProductFormVariants
                          handleChange={handleChange}
                          product={product}
                          selectedOptions={selectedOptions}
                          selectableOptions={product.options[0]}
                          variant={variant}
                        />
                      </VStack>
                      <VStack alignItems="flex-end" justifyContent="flex-start" flexShrink={0}>
                        <ProductFormPrice justifyContent="flex-end" size="3xl" lineHeight="normal" color={"grey.coal"} w="full" />
                      </VStack>
                    </HStack>
                  </VStack>
                  <FormButton
                    type="submit"
                    variant="findInStoreAddToCart"
                    borderRadius="8px"
                    w="100%"
                    size="md"
                    pt={3}
                    pb={2.5}
                    px={6}
                    isLoading={loading}
                    onClick={event => {
                      event.stopPropagation()
                      addToCart(id, 1)
                    }}
                    title={locales?.additionalStockAvailable}
                  >
                    {locales?.additionalStockAvailable}
                  </FormButton>
                </>
              )}
            </VStack>
          </HStack>
          {variant?.sku && <ProductStockOverlayForm id={variant.id} sku={variant?.sku} />}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
})
